
import React from "react";
import { Link, useParams } from "react-router-dom";
import { EmptyState, ParentPageHeader, PortalCard, PortalLoading, StatusBadge, money, niceDate, useParentPortal } from "./parentPortalUtils";

function ParentChildDashboardPage() {
  const { studentId } = useParams();
  const { data, loading, reload } = useParentPortal(`parent-portal/children/${studentId}/dashboard/`);
  if (loading && !data) return <PortalLoading label="Loading child dashboard..." />;

  const student = data?.student || {};
  const fee = data?.fee_status || {};
  const timetable = data?.today_timetable || [];
  const resources = data?.learning?.resources || [];
  const sessions = data?.results?.sessions || [];
  const events = data?.events_sports?.events || [];
  const fixtures = data?.events_sports?.fixtures || [];
  const conduct = data?.conduct?.summary || {};

  return (
    <div className="container-fluid py-4">
      <ParentPageHeader
        title={student.full_name || "Child Dashboard"}
        subtitle={`${student.student_number || "No student number"} · ${student.class_group?.name || "No class group assigned"}`}
        icon="bi-mortarboard"
        actions={<><button className="btn btn-light border" onClick={reload}>Refresh</button><Link className="btn btn-success" to="/parent/children">All Children</Link></>}
      />

      <div className="row g-3 mb-3">
        <div className="col-md-3"><PortalCard title="Fee Status" icon="bi-cash-stack" to={`/parent/children/${studentId}/finance`}><StatusBadge value={fee.status_label}/><h4 className="fw-bold mt-2">{money(fee.outstanding_balance)}</h4><small className="text-muted">Outstanding balance</small></PortalCard></div>
        <div className="col-md-3"><PortalCard title="Today" icon="bi-calendar2-week" to={`/parent/children/${studentId}/timetable`}><h4 className="fw-bold">{timetable.length}</h4><small className="text-muted">timetable item(s)</small></PortalCard></div>
        <div className="col-md-3"><PortalCard title="Reports" icon="bi-file-earmark-medical" to={`/parent/children/${studentId}/results`}><h4 className="fw-bold">{sessions.length}</h4><small className="text-muted">published session(s)</small></PortalCard></div>
        <div className="col-md-3"><PortalCard title="Conduct" icon="bi-shield-check" to={`/parent/children/${studentId}/conduct`}><h4 className="fw-bold">{conduct.open_incidents || 0}</h4><small className="text-muted">open incident(s)</small></PortalCard></div>
      </div>

      <div className="row g-3">
        <div className="col-xl-6">
          <PortalCard title="Today’s Timetable" icon="bi-clock" to={`/parent/children/${studentId}/timetable`}>
            {timetable.length === 0 ? <EmptyState title="No timetable today" /> : timetable.slice(0, 6).map((slot) => (
              <div className="d-flex justify-content-between border-bottom py-2" key={slot.id}>
                <div><strong>{slot.period}</strong><br/><small className="text-muted">{slot.period_time}</small></div>
                <div className="text-end">{slot.subject}<br/><small className="text-muted">{slot.teacher || slot.room || "-"}</small></div>
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="Learning" icon="bi-play-circle" to={`/parent/children/${studentId}/learning`}>
            {resources.length === 0 ? <EmptyState title="No resources" /> : resources.slice(0, 5).map((item) => (
              <div className="border-bottom py-2" key={item.id}>
                <div className="fw-semibold">{item.title}</div>
                <small className="text-muted">{item.subject || "General"} · {item.resource_type}</small>
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="Results & Reports" icon="bi-journal-check" to={`/parent/children/${studentId}/results`}>
            {sessions.length === 0 ? <EmptyState title="No published reports" /> : sessions.map((session) => (
              <div className="d-flex justify-content-between border-bottom py-2" key={session.id}>
                <div><div className="fw-semibold">{session.name}</div><small className="text-muted">{session.marks_count} mark records</small></div>
                <StatusBadge value={session.can_view ? "Available" : "Restricted"} />
              </div>
            ))}
          </PortalCard>
        </div>
        <div className="col-xl-6">
          <PortalCard title="Events & Sports" icon="bi-calendar-event" to={`/parent/children/${studentId}/events-sports`}>
            {[...events, ...fixtures].length === 0 ? <EmptyState title="No events" /> : [...events, ...fixtures].slice(0, 6).map((item, index) => (
              <div className="border-bottom py-2" key={`${item.id}-${index}`}>
                <div className="fw-semibold">{item.title}</div>
                <small className="text-muted">{niceDate(item.start_date || item.fixture_date)}</small>
              </div>
            ))}
          </PortalCard>
        </div>
      </div>
    </div>
  );
}

export default ParentChildDashboardPage;
